// Reports → Games played (F-CAT-04, spec/17 R-04): which titles the venue's PCs ran, from the plays the stations
// detected during sessions — the most played titles as ranked bars (time, share, sessions, players), the time played
// each day or week (the venue's business days, oldest first), and each PC with what was played on it. Per venue:
// rolled up across venues it reads this venue. `reports:read`; the server checks it again.

import type { GamesPlayedReport } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { BarChart, ChartTable, ColumnChart, EmptyState, SegmentedControl, StatCard, type TableColumn } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { NavIcon } from '../../shell/nav-icons';
import { periodsInRange, playTimeOf } from './games-played';
import { trendSummary } from './OverviewTab';
import { KpiFigure, ReportCard, ReportGrid, ReportKpis, ReportPage } from './report-kit';
import { useGamesPlayedReport } from './queries';
import { NO_DATA, shareWords, sparkOf, TabPending } from './tab-kit';

import './reports.css';

type TitleRow = GamesPlayedReport['titles'][number];
type StationRow = GamesPlayedReport['stations'][number];

export function GamesPlayedTab({ organizationId, venueId, from, to, rolledUp }: { readonly organizationId: string; readonly venueId: string; readonly from: string; readonly to: string; readonly rolledUp: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const games = useGamesPlayedReport(organizationId, venueId, from, to, true);
  return (
    <>
      {rolledUp ? <p className="console-subtle console-rt-note">{t('reports.gamesPlayed.thisVenue')}</p> : null}
      {games.data === undefined ? (
        <TabPending query={games} unavailable={t('reports.gamesPlayed.unavailable')} forbidden={t('reports.notPermitted')} />
      ) : games.data.totalSeconds === 0 ? (
        <EmptyState icon={<NavIcon name="reports" />} title={t('reports.gamesPlayed.emptyTitle')} description={t('reports.gamesPlayed.empty')} />
      ) : (
        <GamesPlayed report={games.data} from={from} to={to} />
      )}
    </>
  );
}

/** Play time as a venue reads it: "48 min" under an hour, "12.5 h" from an hour up. */
export function usePlayTime(): (seconds: number) => string {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  return (seconds: number) => {
    const time = playTimeOf(seconds);
    return time.unit === 'minutes'
      ? t('reports.gamesPlayed.minutes', { minutes: format.number(time.value) })
      : t('reports.gamesPlayed.hours', { hours: format.number(time.value, { maximumFractionDigits: 1 }) });
  };
}

/** The report itself: the four figures, then the titles, the days and the PCs. */
function GamesPlayed({ report, from, to }: { readonly report: GamesPlayedReport; readonly from: string; readonly to: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const playTime = usePlayTime();
  const [by, setBy] = useState<'day' | 'week'>('day');
  const names = new Map(report.titles.map((title) => [title.titleId, title.name]));
  const top = report.titles[0];
  const titles = report.titles.filter((title) => title.seconds > 0);
  const stations = report.stations.filter((station) => station.seconds > 0);
  const periods = periodsInRange(report, by, from, to);
  const periodTitle = by === 'day' ? t('reports.gamesPlayed.heading.days') : t('reports.gamesPlayed.heading.weeks');
  const periodLabel = (key: string) => (by === 'day' ? format.date(`${key}T12:00:00Z`, 'medium') : t('reports.gamesPlayed.weekOf', { date: format.date(`${key}T12:00:00Z`, 'medium') }));
  const stationName = (row: StationRow) => row.name ?? (row.number === null ? t('reports.gamesPlayed.stationGone') : t('reports.gamesPlayed.stationNumbered', { number: row.number }));

  const titleColumns: readonly TableColumn<TitleRow>[] = [
    { key: 'title', header: t('reports.gamesPlayed.column.title'), cell: (row) => row.name },
    { key: 'hours', header: t('reports.gamesPlayed.column.hours'), numeric: true, cell: (row) => <span className="console-num">{playTime(row.seconds)}</span> },
    { key: 'share', header: t('reports.gamesPlayed.column.share'), numeric: true, cell: (row) => <span className="console-num">{shareWords(format, report.totalSeconds === 0 ? 0 : row.seconds / report.totalSeconds)}</span> },
    { key: 'sessions', header: t('reports.gamesPlayed.column.sessions'), numeric: true, cell: (row) => <span className="console-num">{format.number(row.sessions)}</span> },
    { key: 'players', header: t('reports.gamesPlayed.column.players'), numeric: true, cell: (row) => <span className="console-num">{format.number(row.players)}</span> },
  ];
  const stationColumns: readonly TableColumn<StationRow>[] = [
    { key: 'station', header: t('reports.gamesPlayed.column.station'), cell: (row) => stationName(row) },
    { key: 'hours', header: t('reports.gamesPlayed.column.hours'), numeric: true, cell: (row) => <span className="console-num">{playTime(row.seconds)}</span> },
    { key: 'titles', header: t('reports.gamesPlayed.column.topTitles'), cell: (row) => <span className="console-muted">{topTitlesOf(row)}</span> },
  ];
  function topTitlesOf(row: StationRow): string {
    return row.titles
      .slice(0, 3)
      .map((share) => `${names.get(share.titleId) ?? share.titleId} (${playTime(share.seconds)})`)
      .join(', ');
  }

  return (
    <ReportPage>
      <ReportKpis label={t('reports.gamesPlayed.title')}>
        <StatCard label={t('reports.gamesPlayed.figure.hours')} value={<KpiFigure value={report.totalSeconds} format={playTime} />} {...sparkOf(periodsInRange(report, 'day', from, to).map((day) => day.seconds))} />
        <StatCard label={t('reports.gamesPlayed.figure.players')} value={<KpiFigure value={report.players} format={(n) => format.number(n)} />} />
        <StatCard label={t('reports.gamesPlayed.figure.titles')} value={<KpiFigure value={titles.length} format={(n) => format.number(n)} />} />
        <StatCard
          label={t('reports.gamesPlayed.figure.top')}
          value={<span className="console-rt-figure-name">{top === undefined ? NO_DATA : top.name}</span>}
          {...(top === undefined ? {} : { hint: t('reports.gamesPlayed.topHint', { time: playTime(top.seconds), share: shareWords(format, report.totalSeconds === 0 ? 0 : top.seconds / report.totalSeconds) }) })}
        />
      </ReportKpis>

      <ReportGrid layout="lead">
        <ReportCard title={t('reports.gamesPlayed.heading.titles')} description={t('reports.gamesPlayed.hint')}>
          <BarChart
            label={t('reports.gamesPlayed.caption.titles')}
            limit={8}
            rows={titles.map((title) => ({
              key: title.titleId,
              label: title.name,
              value: title.seconds,
              display: playTime(title.seconds),
              meta: t('reports.gamesPlayed.titleMeta', {
                share: shareWords(format, report.totalSeconds === 0 ? 0 : title.seconds / report.totalSeconds),
                sessions: t('reports.gamesPlayed.sessionsCount', { count: title.sessions }),
                players: t('reports.gamesPlayed.playersCount', { count: title.players }),
              }),
            }))}
          />
          <ChartTable caption={t('reports.gamesPlayed.caption.titles')} columns={titleColumns} rows={titles} rowKey={(row) => row.titleId} empty={t('reports.gamesPlayed.empty')} />
        </ReportCard>
        <ReportCard
          title={periodTitle}
          description={t('reports.businessDays')}
          action={
            <SegmentedControl<'day' | 'week'>
              label={t('reports.gamesPlayed.view.label')}
              value={by}
              onValueChange={setBy}
              options={[
                { value: 'day', label: t('reports.gamesPlayed.view.days') },
                { value: 'week', label: t('reports.gamesPlayed.view.weeks') },
              ]}
            />
          }
        >
          <ColumnChart
            label={periodTitle}
            summary={trendSummary(t, periods.map((period) => period.seconds), (index) => periodLabel(periods[index]?.key ?? ''), playTime)}
            categories={periods.map((period) => format.custom(`${period.key}T12:00:00Z`, { day: 'numeric', month: 'short' }))}
            categoryLabel={(index) => {
              const period = periods[index];
              if (period === undefined) return '';
              const name = period.topTitleId === null ? undefined : names.get(period.topTitleId);
              return name === undefined ? periodLabel(period.key) : t('reports.gamesPlayed.periodTop', { period: periodLabel(period.key), title: name });
            }}
            series={[{ key: 'hours', label: t('reports.gamesPlayed.figure.hours'), tone: 1, values: periods.map((period) => period.seconds / 3600) }]}
            format={(hours) => playTime(Math.round(hours * 3600))}
            formatTick={(hours) => t('reports.gamesPlayed.hours', { hours: format.number(hours, { maximumFractionDigits: 1 }) })}
          />
          <ChartTable
            caption={t(`reports.gamesPlayed.caption.${by === 'day' ? 'days' : 'weeks'}`)}
            columns={[
              { key: 'period', header: by === 'day' ? t('reports.column.day') : t('reports.column.week'), cell: (row: (typeof periods)[number]) => periodLabel(row.key) },
              { key: 'hours', header: t('reports.gamesPlayed.column.hours'), numeric: true, cell: (row: (typeof periods)[number]) => <span className="console-num">{playTime(row.seconds)}</span> },
              { key: 'top', header: t('reports.gamesPlayed.figure.top'), cell: (row: (typeof periods)[number]) => (row.topTitleId === null ? NO_DATA : (names.get(row.topTitleId) ?? NO_DATA)) },
            ]}
            rows={periods}
            rowKey={(row) => row.key}
          />
        </ReportCard>
      </ReportGrid>

      <ReportCard title={t('reports.gamesPlayed.heading.stations')} description={t('reports.gamesPlayed.stationsHint')}>
        <BarChart
          label={t('reports.gamesPlayed.caption.stations')}
          limit={8}
          rows={stations.map((station) => ({ key: station.stationId, label: stationName(station), value: station.seconds, display: playTime(station.seconds), meta: topTitlesOf(station) }))}
        />
        <ChartTable caption={t('reports.gamesPlayed.caption.stations')} columns={stationColumns} rows={stations} rowKey={(row) => row.stationId} empty={t('reports.gamesPlayed.empty')} />
      </ReportCard>
    </ReportPage>
  );
}
